// SortHeader — klikbare tabel-header die asc/desc/off cycle't.
// Gebruik in <th>: <SortHeader sort={sort} setSort={setSort} field="email">Email</SortHeader>

export interface SortState<F extends string = string> {
  field: F | null;
  dir: "asc" | "desc";
}

interface Props<F extends string> {
  field: F;
  sort: SortState<F>;
  setSort: (s: SortState<F>) => void;
  children: React.ReactNode;
  /** Default sort-direction bij eerste klik (default: asc) */
  defaultDir?: "asc" | "desc";
}

export function SortHeader<F extends string>({ field, sort, setSort, children, defaultDir = "asc" }: Props<F>) {
  const active = sort.field === field;
  const arrow = !active ? "↕" : sort.dir === "asc" ? "↑" : "↓";

  const onClick = () => {
    if (!active) setSort({ field, dir: defaultDir });
    else if (sort.dir === defaultDir) setSort({ field, dir: defaultDir === "asc" ? "desc" : "asc" });
    else setSort({ field: null, dir: "asc" });  // cycle naar uit
  };

  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        font: "inherit",
        color: active ? "var(--color-accent)" : "inherit",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
      }}
    >
      {children}
      <span style={{
        fontSize: 10,
        opacity: active ? 1 : 0.4,
      }}>{arrow}</span>
    </button>
  );
}

/** Generic sort-comparator. Werkt voor strings, numbers en Date-ISO-strings. */
export function compareSort<T>(a: T, b: T, field: keyof T, dir: "asc" | "desc"): number {
  const av = a[field];
  const bv = b[field];
  if (av == null && bv == null) return 0;
  if (av == null) return dir === "asc" ? 1 : -1;
  if (bv == null) return dir === "asc" ? -1 : 1;
  let cmp: number;
  if (typeof av === "number" && typeof bv === "number") cmp = av - bv;
  else if (typeof av === "boolean" && typeof bv === "boolean") cmp = (av === bv) ? 0 : av ? -1 : 1;
  else cmp = String(av).localeCompare(String(bv), "nl");
  return dir === "asc" ? cmp : -cmp;
}
